/**
 * LiveScreen — name-anchored countdown, day-timeline progress bar, ghost end-retreat
 * affordance, hold-to-end, bell-in-container transition.
 *
 * UI-INV-005: Leaf component — props only, no engine imports.
 * screen-integration-004 · redesign.
 */
import { useRef, useCallback, useEffect, useState } from "react";
import styles from "./LiveScreen.module.css";
import { IntentStrip } from "../components/IntentStrip";
import { LiveScheduleSheet } from "./LiveScheduleSheet";
import type { SheetSession } from "./LiveScheduleSheet";
import { TEST_IDS } from "../testing/testIds";
import { STORAGE_KEYS, TIMING } from "../constants";

function formatTimer(seconds: number): string {
  const clamped = Math.max(0, Math.round(seconds));
  const mm = Math.floor(clamped / 60).toString().padStart(2, "0");
  const ss = (clamped % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

// Ghost affordance timing: reveal after ~10 minutes, then fade after ~6 seconds
const GHOST_REVEAL_AFTER_SECONDS = TIMING.GHOST_REVEAL_AFTER_MS / 1000;
const GHOST_VISIBLE_MS = TIMING.GHOST_VISIBLE_MS;
const DIM_AMOUNTS = TIMING.HOLD_DIM_STEPS;

/** One scheduled session's position on the day timeline, pre-computed for rendering. */
type Segment = { start: number; end: number; mid: number; name: string; status: string };

// Best-effort duration (in seconds) for a schedule row, used to place timeline ticks.
// Maps whatever duration field SheetSession carries; falls back to 0 (skipped).
function sessionDurationSeconds(s: SheetSession): number {
  const any = s as unknown as Record<string, number | undefined>;
  if (any.durationSeconds != null) return any.durationSeconds;
  if (any.durationMinutes != null) return any.durationMinutes * 60;
  return 0;
}

type Props = {
  session: { name: string; kind: string };
  remainingSeconds: number;
  elapsedSeconds: number;
  totalDurationSeconds: number;
  dayProgress: { idx: number; count: number };
  endsAtLabel: string;
  retreatStartLabel: string;
  retreatEndsLabel: string;
  intentLabels: string[];
  daySchedule: SheetSession[];
  whisperLine?: string;
  lastBellFiredAt?: number | null;
  onEndRetreat?: () => void;
  /** True when the AudioContext is suspended and auto-recovery has not yet succeeded. */
  audioSuspended?: boolean;
  /** Called when the user taps the audio-recovery banner — must be a user gesture. */
  onResumeAudio?: () => void;
};

export function LiveScreen({
  session,
  remainingSeconds,
  elapsedSeconds,
  totalDurationSeconds,
  dayProgress,
  endsAtLabel,
  retreatStartLabel,
  retreatEndsLabel,
  intentLabels,
  daySchedule,
  whisperLine,
  lastBellFiredAt,
  onEndRetreat,
  audioSuspended = false,
  onResumeAudio,
}: Props) {
  // ── Progress bar + schedule sheet ──────────────────────────────────────────
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleHintSeen, setScheduleHintSeen] = useState(
    () => localStorage.getItem(STORAGE_KEYS.SCHEDULE_HINT_SEEN) === "true",
  );

  const handleProgressBarTap = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      setShowSchedule((s) => !s);
      if (!scheduleHintSeen) {
        setScheduleHintSeen(true);
        try { localStorage.setItem(STORAGE_KEYS.SCHEDULE_HINT_SEEN, "true"); } catch { /* quota */ }
      }
    },
    [scheduleHintSeen],
  );

  // ── Ghost end-retreat affordance ────────────────────────────────────────────
  // Fades in once, after ~10 minutes of sitting; lingers a few seconds; fades out.
  // A whisper, not an instruction.
  const [endGhost, setEndGhost] = useState(false);
  const ghostShownRef = useRef(false);
  useEffect(() => {
    if (ghostShownRef.current) return;
    if (elapsedSeconds < GHOST_REVEAL_AFTER_SECONDS) return;
    ghostShownRef.current = true;
    setEndGhost(true);
    const t = setTimeout(() => setEndGhost(false), GHOST_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [elapsedSeconds]);

  // ── Bell-in-container transition ───────────────────────────────────────────
  const [transitioning, setTransitioning] = useState(false);
  const [previousSessionName, setPreviousSessionName] = useState("");
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSessionRef = useRef(session.name);

  useEffect(() => {
    if (lastBellFiredAt == null) return;
    setPreviousSessionName(prevSessionRef.current);
    setTransitioning(true);
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = setTimeout(() => setTransitioning(false), TIMING.BELL_TRANSITION_MS);
  }, [lastBellFiredAt]);

  useEffect(() => {
    prevSessionRef.current = session.name;
  }, [session.name]);

  // ── Hold-to-end 4-frame dim ────────────────────────────────────────────────
  const [holdProgress, setHoldProgress] = useState<0 | 1 | 2 | 3>(0);
  const holdTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearHoldTimers = useCallback(() => {
    holdTimersRef.current.forEach(clearTimeout);
    holdTimersRef.current = [];
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest("[data-no-hold]")) return;
      clearHoldTimers();
      holdTimersRef.current = [
        setTimeout(() => setHoldProgress(1), TIMING.LIVE_HOLD_STEP1_MS),
        setTimeout(() => setHoldProgress(2), TIMING.LIVE_HOLD_STEP2_MS),
        setTimeout(() => {
          setHoldProgress(3);
          onEndRetreat?.();
        }, TIMING.LIVE_END_HOLD_MS),
      ];
    },
    [clearHoldTimers, onEndRetreat],
  );

  const handlePointerUp = useCallback(() => {
    clearHoldTimers();
    setHoldProgress(0);
  }, [clearHoldTimers]);

  // ── Derived display values ─────────────────────────────────────────────────
  const progressPct =
    totalDurationSeconds > 0
      ? Math.min(100, (elapsedSeconds / totalDurationSeconds) * 100)
      : 0;

  const atMidpoint =
    totalDurationSeconds > 0 && elapsedSeconds >= totalDurationSeconds * 0.5;

  // Timeline segments: one per scheduled session, carrying name + boundary fractions.
  const segments: Segment[] = (() => {
    const total = daySchedule.reduce((a, s) => a + sessionDurationSeconds(s), 0);
    if (total <= 0) return [];
    const out: Segment[] = [];
    let acc = 0;
    for (const s of daySchedule) {
      const dur = sessionDurationSeconds(s);
      const start = acc / total;
      const end = (acc + dur) / total;
      out.push({ start, end, mid: (start + end) / 2, name: s.name, status: s.status });
      acc += dur;
    }
    return out;
  })();
  // Tick boundaries: between adjacent segments (all except the last)
  const tickFractions = segments.slice(0, -1).map((s) => s.end);

  const dimAmount = DIM_AMOUNTS[holdProgress];
  const dayN = dayProgress.idx + 1;
  const dayCount = dayProgress.count;

  return (
    <div
      className={styles.runtime}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      data-screen-label="Live"
      data-testid={TEST_IDS.LIVE_SCREEN}
    >
      {/* Audio recovery — shown when context is suspended and auto-resume failed */}
      {audioSuspended && onResumeAudio && (
        <button
          className={styles.audioRecoveryBanner}
          onClick={onResumeAudio}
          type="button"
          data-testid={TEST_IDS.LIVE_AUDIO_RECOVERY}
          data-no-hold
        >
          tap to restore bell sound
        </button>
      )}

      {/* Hold-to-end dim overlay */}
      {dimAmount > 0 && (
        <div
          className={styles.holdOverlay}
          style={{ opacity: dimAmount }}
          data-testid={TEST_IDS.LIVE_HOLD_OVERLAY}
          aria-hidden="true"
        />
      )}

      {/* Top strip: IntentStrip + day label */}
      <div className={styles.topStrip}>
        <IntentStrip intents={intentLabels} style={{ flex: 1 }} />
        {dayCount > 1 && (
          <span className={styles.dayLabel}>
            day {dayN} / {dayCount}
          </span>
        )}
      </div>

      {/* Vertically centered block — the session NAME is the anchor */}
      <div className={styles.center}>
        {transitioning ? (
          <div className={styles.bellTransition} data-testid={TEST_IDS.LIVE_BELL_TRANSITION}>
            <div className={styles.previousSession}>{previousSessionName}</div>
            <div className={styles.sessionHeading}>{session.name}</div>
            <div className={styles.sessionTerm}>{session.kind}</div>
            <div className={styles.gradientSweep} aria-hidden="true" />
            <div className={styles.bellLabel}>just now</div>
          </div>
        ) : (
          <>
            <div className={styles.eyebrow}>Now</div>
            <div className={styles.sessionName}>{session.name}</div>
            <div className={styles.sessionTerm}>{session.kind}</div>
            <div className={styles.timer} data-testid={TEST_IDS.LIVE_TIMER}>
              {formatTimer(remainingSeconds)}
            </div>
            <div className={styles.endsAt}>until {endsAtLabel}</div>
            {atMidpoint && whisperLine && (
              <div className={styles.midpointWhisper}>{whisperLine}</div>
            )}
          </>
        )}
      </div>

      {/* Ghost end-retreat affordance — fades in after ~10 min, then fades out */}
      <div
        className={`${styles.endGhost} ${endGhost && holdProgress === 0 ? styles.endGhostOn : ""}`}
        data-testid={TEST_IDS.LIVE_HOLD_TUTORIAL}
        aria-hidden="true"
      >
        hold&nbsp;·&nbsp;to end retreat
      </div>

      {/* Hold-to-end reassurance — first (light) dim step only */}
      {holdProgress === 1 && (
        <div className={styles.holdMsg}>Take your time.</div>
      )}

      {/* Progress bar — a day timeline. Tap to open the schedule. */}
      <div className={styles.progressArea} data-no-hold>
        <div
          className={styles.progressTap}
          onPointerDown={handleProgressBarTap}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowSchedule((s) => !s); } }}
          data-no-hold
          aria-label="View schedule"
          role="button"
          tabIndex={0}
          data-testid={TEST_IDS.LIVE_PROGRESS_BAR}
        >
          {/* Session name labels — centered above each segment */}
          {segments.length > 0 && (
            <div className={styles.trackLabels} aria-hidden="true">
              {segments.map((seg, i) => (
                <span
                  key={i}
                  className={`${styles.trackLabel} ${
                    seg.status === "done" ? styles.trackLabelPast
                    : seg.status === "current" ? styles.trackLabelNow
                    : ""
                  }`}
                  style={{ left: `${seg.mid * 100}%` }}
                >
                  {seg.name}
                </span>
              ))}
            </div>
          )}
          <div className={styles.track}>
            <div
              className={styles.trackFill}
              style={{ width: `${progressPct}%` }}
              data-testid={TEST_IDS.LIVE_PROGRESS_FILL}
            />
            {tickFractions.map((p, i) => (
              <span
                key={i}
                className={`${styles.tick} ${p <= progressPct / 100 ? styles.tickPast : ""}`}
                style={{ left: `${p * 100}%` }}
                aria-hidden="true"
              />
            ))}
            <span className={styles.trackHead} style={{ left: `${progressPct}%` }} aria-hidden="true" />
          </div>
          <div className={styles.progressLabels}>
            <span>{retreatStartLabel}</span>
            {!scheduleHintSeen && (
              <span className={styles.progressTapHint} data-testid={TEST_IDS.LIVE_SCHEDULE_HINT}>tap for schedule</span>
            )}
            <span>{retreatEndsLabel} · ends</span>
          </div>
        </div>
      </div>

      {/* Pause expectation — permanent quiet reassurance */}
      <div className={styles.pauseNote} data-testid={TEST_IDS.LIVE_PAUSE_NOTE}>
        If you step away, the schedule continues. Return when ready.
      </div>

      {/* LiveScheduleSheet overlay */}
      {showSchedule && (
        <LiveScheduleSheet
          sessions={daySchedule}
          intentLabels={intentLabels}
          dayLabel={`Day ${dayN} of ${dayCount}`}
          retreatEndsLabel={retreatEndsLabel}
          whisperLine={whisperLine}
          onDismiss={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
}
